import { api as $axios } from '@/helpers/interceptors'
import {
  REPORT_PRODUCT_QUOTA_TIME,
  EXPORT_REPORT_PRODUCT_QUOTA_TIME,
  REPORT_LOT_OF_PRODUCT_QUOTA,
} from '@/constants/path'
import { meta } from '@/config/pagination'
import { numberFormat } from '@/utils/utils'
import i18n from '../plugins/i18n'
import snakeCaseKeys from 'snakecase-keys'
import dayjs from 'dayjs'

const setUnLoading = (commit, status = false) => {
  commit('SET_STATE', {
    key: 'isLoading',
    value: status,
  })
}

export const freezeHeaderProductQuota = [
  {
    label: i18n.t('label.all'),
    key: 'total',
    data: 0,
    amount: 0,
    color: 'black',
  },
  {
    label: i18n.t('label.orderInprogress'),
    key: 'process',
    data: 0,
    amount: 0,
    color: 'warning',
    icon: 'mdi-cart-outline',
  },
  {
    label: i18n.t('label.cancel'),
    key: 'return',
    data: 0,
    amount: 0,
    color: 'red',
    icon: 'mdi-cancel',
  },
  {
    label: i18n.t('label.complete'),
    key: 'complete',
    data: 0,
    amount: 0,
    color: 'success',
    icon: 'mdi-check-circle-outline',
  },
  {
    label: i18n.t('label.depositOrder'),
    key: 'depositOrder',
    data: 0,
    amount: 0,
    color: 'black',
  },
  {
    label: i18n.t('label.fullOrder'),
    key: 'fullOrder',
    data: 0,
    amount: 0,
    color: 'black',
  },
]

const state = () => ({
  productQuotaList: [],
  headerProductQuota: Array.from(freezeHeaderProductQuota),
  meta,
  loadingDownloadFile: false,
})

// getters
const getters = {
  productQuotaListV2: (state) => {
    return state.productQuotaList.map((item) => {
      if (item.quota && Array.isArray(item.quota)) {
        item.quota.map((qt) => {
          item[qt.date] = { ...qt }
        })
      }
      return item
    })
  },
}

// actions
const actions = {
  // eslint-disable-next-line no-empty-pattern
  async getReportProductQuota({ commit }, params) {
    setUnLoading(commit, true)
    try {
      const res = await $axios(
        `${REPORT_PRODUCT_QUOTA_TIME}/${params?.query?.campaignId}`,
        'get',
        {
          params: snakeCaseKeys({
            orderCreate: params?.startDate,
            orderCreateEnd: params?.endDate,
            ...(params?.query.condition && {
              condition: params?.query.condition,
            }),
          }),
        },
      )
      const data = res?.data

      const list = res?.data?.sku.filter((x) => {
        if (
          params?.nameOrSku &&
          (x.name
            .toLowerCase()
            .includes(params?.nameOrSku.toLowerCase()) ||
            x.sku
              .toLowerCase()
              .includes(params?.nameOrSku.toLowerCase()))
        )
          return true
        else if (!params?.nameOrSku) return true
      })

      commit('SET_STATE', {
        key: 'productQuotaList',
        value: list || [],
      })

      commit('SET_HEADER_PRODUCT_QUOTA', data)
      setUnLoading(commit, false)
      return data
    } catch (error) {
      setUnLoading(commit, false)
      commit('SET_STATE', {
        key: 'productQuotaList',
        value: [],
      })
    }
  },

  // eslint-disable-next-line no-unused-vars
  async exportReportProductQuota({ commit }, params) {
    try {
      commit('SET_STATE', { key: 'loadingDownloadFile', value: true })
      await $axios(
        `${EXPORT_REPORT_PRODUCT_QUOTA_TIME}/${params?.query?.campaignId}`,
        'post',
        snakeCaseKeys({
          orderCreate: params?.startDate,
          orderCreateEnd: params?.endDate,
          ...(params?.query.condition && {
            condition: params?.query.condition,
          }),
        }),
        {
          responseType: 'blob',
        },
      ).then((response) => {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `product-quota-${params?.startDate}-to-${
          params?.endDate
        }-${dayjs().format('YYYYMMDDHHmmss')}` // Use .xlsx extension for Excel files
        document.body.appendChild(link) // Append the link to the body
        link.click() // Trigger the download
        document.body.removeChild(link) // Remove the link from the body
        URL.revokeObjectURL(link.href) // Revoke the object URL to free up memory
      })
      commit('SET_STATE', {
        key: 'loadingDownloadFile',
        value: false,
      })
    } catch (error) {
      commit('SET_STATE', {
        key: 'loadingDownloadFile',
        value: false,
      })
      return new Error(error)
    }
  },

  // eslint-disable-next-line no-unused-vars
  async exportReportLotOfProductQuota({ commit }, params) {
    try {
      commit('SET_STATE', { key: 'loadingDownloadFile', value: true })
      await $axios(
        REPORT_LOT_OF_PRODUCT_QUOTA,
        'post',
        { slug: params.slug },
        {
          responseType: 'blob',
        },
      ).then((response) => {
        const blob = new Blob([response.data], {
          type: 'text/csv',
        })

        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `[LOT]-${params.bu}-${
          params.slug
        }-${dayjs().format('YYYYMMDDHHmmss')}` // Use .csv
        document.body.appendChild(link) // Append the link to the body
        link.click() // Trigger the download
        document.body.removeChild(link) // Remove the link from the body
        URL.revokeObjectURL(link.href) // Revoke the object URL to free up memory
      })
      commit('SET_STATE', {
        key: 'loadingDownloadFile',
        value: false,
      })
    } catch (error) {
      commit('SET_STATE', {
        key: 'loadingDownloadFile',
        value: false,
      })
      return new Error(error)
    }
  },
}

// mutations
const mutations = {
  SET_STATE(state, ops) {
    state[ops.key] = ops.value
  },

  SET_HEADER_PRODUCT_QUOTA(state, value) {
    state.headerProductQuota[0].data = numberFormat(value.total)
    state.headerProductQuota[1].data = numberFormat(value.process)
    state.headerProductQuota[2].data = numberFormat(value.return)
    state.headerProductQuota[3].data = numberFormat(value.complete)
    state.headerProductQuota[4].data = numberFormat(
      value.depositOrder,
    )
    state.headerProductQuota[4].amount =
      numberFormat(value.depositAmount) ?? 0

    state.headerProductQuota[5].data = numberFormat(value.fullOrder)
    state.headerProductQuota[5].amount =
      numberFormat(value.fullAmount) ?? 0
  },
  CLEAR_HEADER_PRODUCT_QUOTA(state) {
    for (const item of freezeHeaderProductQuota) {
      item.data = 0
      item.amount = 0
    }
    state.headerProductQuota = freezeHeaderProductQuota
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
